import { API_ROOT } from '../../constants'
import api from '../../utils/api'
import axios from 'axios'
import JSZip from 'jszip'
import { isVideoFile } from 'helpers/file-type'
import { getFilesMetadata, handlePresignedUploads } from '../presigned-uploads/handlePresignedUploads'
import { createSite, updateSite } from 'services/site'
import { handleChunkUploads, CHUNK_UPLOAD_THRESHOLD } from '../chunk-uploads/handleChunkUploads'
import { generateRandomSubdomain } from 'utils/general'

const containsVideoFiles = (files = []) => {
  if (!Array.isArray(files)) return false

  return files.some((file) => {
    if (!file || typeof file !== 'object') {
      return false
    }

    const fileName = typeof file.name === 'string' ? file.name : ''
    const fileType = typeof file.type === 'string' ? file.type : ''

    if (!fileName && !fileType) {
      return false
    }

    return isVideoFile(fileName, fileType)
  })
}

export const MAX_DIRECT_UPLOAD_SIZE = 2 * 1024 * 1024 * 1024 // 2GB

export const calculateTotalSize = async (files) => {
  let totalSize = 0

  if (!files || !Array.isArray(files)) {
    return 0
  }

  for (const file of files) {
    if (!file) continue
    if (file?.name?.toLowerCase().endsWith('.zip')) {
      try {
        const zip = new JSZip()
        const zipContents = await zip.loadAsync(file)

        for (const zipEntry of Object.values(zipContents.files)) {
          if (!zipEntry.dir) {
            totalSize += zipEntry._data.uncompressedSize
          }
        }
      } catch (error) {
        totalSize += file.size
      }
    } else {
      totalSize += file.size
    }
  }

  return totalSize
}

export const updateSiteContents = async (
  domain,
  body,
  config,
  uploadProgress,
  isPresignedUpload,
  originalFiles,
  dispatch
) => {
  try {
    let formData = body
    const filesToAdd = originalFiles || []
    const newDomain = formData.get('newDomain')

    const totalSize = await calculateTotalSize(filesToAdd)
    const totalFilesMetadata = {
      totalFiles: getFilesMetadata(filesToAdd),
      completedFiles: []
    }

    const isVideoFiles = containsVideoFiles(filesToAdd)

    let uploadedBytes = 0
    const reportProgress = (delta) => {
      if (!uploadProgress || !totalSize) return
      const total = totalSize
      uploadedBytes = Math.min(uploadedBytes + (delta || 0), total)
      uploadProgress({ loaded: uploadedBytes, total }, { size: total })
    }

    const chunkEligibleFiles =
      !isPresignedUpload && !isVideoFiles ? filesToAdd.filter((file) => file?.size > CHUNK_UPLOAD_THRESHOLD) : []
    if (chunkEligibleFiles.length) {
      const baseFormData = new FormData()
      formData.forEach((value, key) => {
        if (key !== 'filesToAdd') {
          baseFormData.append(key, value)
        }
      })

      const chunkUploadReferences = await handleChunkUploads({
        files: chunkEligibleFiles,
        config,
        onProgress: reportProgress
      })

      const remainingFiles = filesToAdd.filter((file) => !chunkEligibleFiles.includes(file))
      remainingFiles.forEach((file) => {
        baseFormData.append('filesToAdd', file)
      })

      if (chunkUploadReferences.length) {
        baseFormData.append('chunkUploadIds', JSON.stringify(chunkUploadReferences))
        totalFilesMetadata.completedFiles.push(...getFilesMetadata(chunkEligibleFiles))
      }

      formData = baseFormData
    }

    if (isPresignedUpload || (!chunkEligibleFiles.length && (totalSize > MAX_DIRECT_UPLOAD_SIZE || isVideoFiles))) {
      formData.append('domain', domain)

      const [firstFile, ...remainingFiles] = filesToAdd

      const result = await handlePresignedUploads({
        files: firstFile ? [firstFile] : [],
        domain,
        newDomain,
        formData,
        config,
        isUpdate: true,
        isUpdateSite: true,
        uploadProgress,
        isLastFile: filesToAdd.length === 1 ? true : false,
        filesToAddPresignedTotalSize: totalSize,
        filesToAddPresignedMetadata: totalFilesMetadata,
        dispatch
      })

      totalFilesMetadata.completedFiles = getFilesMetadata([firstFile])

      if (remainingFiles.length > 0) {
        handlePresignedUploads({
          files: remainingFiles,
          domain,
          newDomain,
          formData,
          config,
          isUpdate: true,
          isUpdateSite: true,
          filesToAddPresignedTotalSize: totalSize,
          filesToAddPresignedMetadata: totalFilesMetadata,
          dispatch
        })
      }

      return result
    }

    const finalUploadProgress = (event) => {
      if (!uploadProgress) return
      const { loaded = 0, total = 0 } = event
      const grandTotal = totalSize || total || 1
      const adjustedLoaded = Math.min(uploadedBytes + loaded, grandTotal)
      uploadProgress({ total: grandTotal, loaded: adjustedLoaded }, { size: grandTotal })
    }

    const response = await updateSite(formData, { ...config, domain }, finalUploadProgress)

    if (uploadProgress && totalSize) {
      uploadProgress({ total: totalSize, loaded: totalSize }, { size: totalSize })
    }

    return response
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const createSiteContents = async (body, config, uploadProgress, dispatch) => {
  try {
    let formData = body
    const filesToAdd = formData.getAll('filesToAdd')
    const domainSuffix = formData.get('domainSuffix')
    const domain =
      formData.get('newDomain') || generateRandomSubdomain() + (domainSuffix ? domainSuffix : '.tiiny.site')

    const totalSize = await calculateTotalSize(filesToAdd)
    const totalFilesMetadata = {
      totalFiles: getFilesMetadata(filesToAdd),
      completedFiles: []
    }

    const isVideoFiles = containsVideoFiles(filesToAdd)

    let uploadedBytes = 0
    const reportProgress = (delta) => {
      if (!uploadProgress || !totalSize) return
      uploadedBytes = Math.min(uploadedBytes + (delta || 0), totalSize)
      uploadProgress({ loaded: uploadedBytes, total: totalSize }, { size: totalSize })
    }

    const chunkEligibleFiles = !isVideoFiles ? filesToAdd.filter((file) => file?.size > CHUNK_UPLOAD_THRESHOLD) : []
    if (chunkEligibleFiles.length) {
      const baseFormData = new FormData()
      formData.forEach((value, key) => {
        if (key !== 'filesToAdd') {
          baseFormData.append(key, value)
        }
      })

      const chunkUploadReferences = await handleChunkUploads({
        files: chunkEligibleFiles,
        config,
        onProgress: reportProgress
      })

      const remainingFiles = filesToAdd.filter((file) => !chunkEligibleFiles.includes(file))
      remainingFiles.forEach((file) => {
        baseFormData.append('filesToAdd', file)
      })

      if (chunkUploadReferences.length) {
        baseFormData.append('chunkUploadIds', JSON.stringify(chunkUploadReferences))
        totalFilesMetadata.completedFiles.push(...getFilesMetadata(chunkEligibleFiles))
      }

      formData = baseFormData
    }

    if (!chunkEligibleFiles.length && (totalSize > MAX_DIRECT_UPLOAD_SIZE || isVideoFiles)) {
      const [firstFile, ...remainingFiles] = filesToAdd

      const result = await handlePresignedUploads({
        files: [firstFile],
        domain,
        newDomain: domain,
        formData,
        config,
        isUpdate: false,
        isUpdateSite: false,
        uploadProgress,
        isLastFile: filesToAdd.length === 1 ? true : false,
        filesToAddPresignedTotalSize: totalSize,
        filesToAddPresignedMetadata: totalFilesMetadata,
        dispatch
      })

      totalFilesMetadata.completedFiles = getFilesMetadata([firstFile])

      if (remainingFiles.length > 0) {
        handlePresignedUploads({
          files: remainingFiles,
          domain,
          newDomain: domain,
          formData,
          config,
          isUpdate: true,
          isUpdateSite: false,
          filesToAddPresignedTotalSize: totalSize,
          filesToAddPresignedMetadata: totalFilesMetadata,
          dispatch
        })
      }

      return result
    }

    const response = await createSite(formData, config, (event) => {
      if (!uploadProgress) return
      const { loaded = 0, total = 0 } = event
      const grandTotal = totalSize || total || 1
      const adjustedLoaded = Math.min(uploadedBytes + loaded, grandTotal)
      uploadProgress({ total: grandTotal, loaded: adjustedLoaded }, { size: grandTotal })
    })

    if (uploadProgress && totalSize) {
      uploadProgress({ total: totalSize, loaded: totalSize }, { size: totalSize })
    }

    return response
  } catch (error) {
    console.error(error)
    throw error
  }
}
